import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import {
  useGetTagsQuery,
  useGetThreadQuery,
  useUpdateSurveyConfigMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useThreadForm, ThreadFormInitialData } from '@/hooks/residents/corkBoard/useThreadForm';
import { CreateThreadForm } from '@/components/residents/corkBoard/CreateThreadForm';
import { SurveyEditPanel } from '@/components/residents/corkBoard/survey/SurveyEditPanel';
import { Button } from '@/components/residents/marketplace/shared/Button';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

type EditThreadRouteProp = RouteProp<RootStackParamList, 'EditThread'>;
type NavigationProp = StackNavigationProp<RootStackParamList, 'EditThread'>;

export const EditThreadScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<EditThreadRouteProp>();
  const { threadId } = route.params;
  const { layoutMode } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;

  const isDesktop = layoutMode === 'desktop';
  const { semantic } = useThemeColors();

  const [isDiscardVisible, setIsDiscardVisible] = useState(false);

  const { data: threadData, isLoading: isLoadingThread, isError } = useGetThreadQuery({
    threadId,
    page: 0,
    pageSize: 1,
  });

  const { data: tags = [], isLoading: isLoadingTags } = useGetTagsQuery(
    { propertyId: propertyId ?? 0 },
    { skip: !propertyId },
  );

  const initialData = useMemo<ThreadFormInitialData | undefined>(() => {
    if (!threadData?.thread) return undefined;
    const thread = threadData.thread;
    return {
      title: thread.title,
      body: thread.body,
      tagIds: thread.tags.map((t) => Number(t.id)),
      attachments: thread.attachments,
    };
  }, [threadData?.thread]);

  const handleSuccess = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const isSurvey = threadData?.thread?.kind === 'survey';

  const form = useThreadForm({
    mode: 'edit',
    threadId,
    initialData,
    onSuccess: handleSuccess,
    isSurvey,
  });

  const surveySummary = threadData?.thread?.survey;

  const [updateSurveyConfig] = useUpdateSurveyConfigMutation();

  const handleSurveyConfigChange = useCallback(
    (patch: { expiresAt?: string | null; allowReplies?: boolean }) => {
      updateSurveyConfig({ threadId, ...patch });
    },
    [threadId, updateSurveyConfig],
  );

  const handleClose = useCallback(() => {
    if (form.isDirty) {
      setIsDiscardVisible(true);
    } else {
      navigation.goBack();
    }
  }, [form.isDirty, navigation]);

  const handleDiscard = useCallback(() => {
    setIsDiscardVisible(false);
    navigation.goBack();
  }, [navigation]);

  const isDataLoading = isLoadingThread || isLoadingTags;
  const canSave = form.isValid && form.isDirty && !form.isSubmitting;

  if (isError && !threadData) {
    return (
      <UnifiedModal
        visible={true}
        variant={isDesktop ? 'dialog' : 'fullscreen'}
        animateEntry={false}
        onClose={() => navigation.goBack()}
        showHeader={false}
        scrollable={false}
      >
        <View style={[styles.header, { borderBottomColor: semantic.border.secondary }]}>
          <TouchableOpacity
            style={styles.headerSide}
            onPress={() => navigation.goBack()}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={18} height={18} color={semantic.text.primary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: semantic.text.primary }]}>Edit Post</Text>
          <View style={styles.headerSide} />
        </View>
        <View style={styles.centeredContainer}>
          <Text style={[styles.errorText, { color: semantic.text.secondary }]}>Failed to load thread</Text>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: semantic.interactive.primary }]}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.retryButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </UnifiedModal>
    );
  }

  return (
    <>
      {isDesktop && (
        <TouchableOpacity
          style={styles.desktopBackdrop}
          activeOpacity={1}
          onPress={handleClose}
        />
      )}

      <UnifiedModal
        visible={true}
        variant={isDesktop ? 'dialog' : 'fullscreen'}
        animateEntry={false}
        onClose={handleClose}
        showHeader={false}
        scrollable={false}
        avoidKeyboard
      >
        <View style={[styles.header, { borderBottomColor: semantic.border.secondary }]}>
          <TouchableOpacity
            style={styles.headerSide}
            onPress={handleClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={18} height={18} color={semantic.text.primary} />
          </TouchableOpacity>

          <Text style={[styles.headerTitle, { color: semantic.text.primary }]}>
            {isSurvey ? 'Edit Survey' : 'Edit Post'}
          </Text>

          <TouchableOpacity
            style={[styles.headerSide, styles.saveButton, { backgroundColor: semantic.interactive.primary }, !canSave && styles.saveButtonDisabled]}
            onPress={form.handleSubmit}
            disabled={!canSave}
            activeOpacity={0.7}
          >
            {form.isSubmitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>Save</Text>
            )}
          </TouchableOpacity>
        </View>

        {isDataLoading || !initialData ? (
          <View style={styles.centeredContainer}>
            <ActivityIndicator size="large" color={semantic.interactive.primary} />
          </View>
        ) : (
          <CreateThreadForm
            mode="edit"
            tags={tags}
            title={form.title}
            onChangeTitle={form.setTitle}
            body={form.body}
            onChangeBody={form.setBody}
            selectedTagIds={form.selectedTagIds}
            onToggleTag={form.toggleTag}
            attachments={form.attachments}
            existingAttachments={form.existingAttachments}
            onAddFiles={form.addFiles}
            onRemoveFile={form.removeFile}
            onRemoveExistingAttachment={form.removeExistingAttachment}
            errors={form.errors}
            hasAttemptedSubmit={form.hasAttemptedSubmit}
            isSubmitting={form.isSubmitting}
            uploadProgress={form.uploadProgress}
            submitError={form.submitError}
            footer={
              isSurvey && surveySummary ? (
                <View style={styles.surveyFooter}>
                  <SurveyEditPanel
                    locked={surveySummary.responseCount > 0}
                    survey={surveySummary}
                    onConfigChange={handleSurveyConfigChange}
                  />
                  <Button
                    title={`View all responses (${surveySummary.responseCount})`}
                    variant="outline"
                    size="medium"
                    fullWidth
                    onPress={() => navigation.navigate('SurveyResponses', { threadId })}
                  />
                </View>
              ) : undefined
            }
          />
        )}
      </UnifiedModal>

      <ConfirmationModal
        visible={isDiscardVisible}
        message="You have unsaved changes. Are you sure you want to discard your edits?"
        onCancel={() => setIsDiscardVisible(false)}
        onProceed={handleDiscard}
        cancelText="Keep Editing"
        proceedText="Discard"
      />

    </>
  );
};

const styles = StyleSheet.create({
  surveyFooter: { gap: 12 },
  desktopBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerSide: {
    minWidth: 60,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
    flex: 1,
  },
  saveButton: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 34,
  },
  saveButtonDisabled: {
    opacity: 0.4,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  errorText: {
    fontSize: 16,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
});
